import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaArrowLeft } from 'react-icons/fa'
import { Button, Checkbox, Input, ModalV2, Select } from '@components'
import {
  createMcpConnection,
  fetchMcpTemplates,
  IMcpCreateInput,
  IMcpSession,
  IMcpTemplate,
  McpAuthType
} from '@query'
import { useBusinessContext } from '@root/context'
import { ProviderAvatar, WarningBanner } from './shared'
import {
  Acknowledge,
  AuthBadge,
  CredsHelp,
  ExternalNote,
  Field,
  FieldHint,
  Form,
  ProviderName,
  SectionLabel,
  SelectedHeader,
  TemplateCard,
  TemplateDesc,
  TemplateGrid,
  TemplateName,
  TemplateText
} from './AddConnectionDialog.styled'

interface Props {
  session: IMcpSession
  returnUrl: string
  onClose: () => void
  onCreated: () => void
}

// Surface the backend's error message (FastAPI `detail`) so the user sees *why* a
// connection failed (e.g. "provide a client_id") instead of a generic error.
const backendDetail = (error: unknown): string | null => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : null
}

// The Authorization scheme sent with a static token, so the user tells us up front
// (Bearer vs Token) instead of trial-and-error.
const TOKEN_SCHEMES = ['Bearer', 'Token']

export const AddConnectionDialog: React.FC<Props> = ({
  session,
  returnUrl,
  onClose,
  onCreated
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const [templateKey, setTemplateKey] = useState<string | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [serverUrl, setServerUrl] = useState('')
  const [authType, setAuthType] = useState<McpAuthType>('oauth')
  const [token, setToken] = useState('')
  const [tokenScheme, setTokenScheme] = useState('Bearer')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  const { data: templates } = useQuery<IMcpTemplate[]>({
    queryKey: [slug, 'luca-mcp-templates'],
    queryFn: () => fetchMcpTemplates(session),
    enabled: Boolean(session.token)
  })

  const selected = useMemo(
    () => templates?.find(item => item.key === templateKey) ?? null,
    [templates, templateKey]
  )

  const selectTemplate = (template: IMcpTemplate) => {
    setTemplateKey(template.key)
    setDisplayName(template.key === 'custom' ? '' : template.name)
    setServerUrl(template.default_server_url ?? '')
    setAuthType(template.auth_type)
  }

  const goBack = () => {
    setTemplateKey(null)
    setAcknowledged(false)
  }

  const effectiveAuthType = selected?.allow_auth_type_override ? authType : selected?.auth_type
  const isCustom = selected?.key === 'custom'
  // Kravia-style connectors collect a client id + secret instead of a single token; the
  // backend base64-encodes the pair into HTTP Basic auth.
  const isBasicPair = selected?.credential === 'basic_pair'
  const blocked = isCustom && !acknowledged

  const createMutation = useMutation({
    mutationFn: (input: IMcpCreateInput) => createMcpConnection(session, input),
    onSuccess: result => {
      if (result.authorize_url) {
        window.location.assign(result.authorize_url)
        return
      }
      const verification = result.verification
      if (verification && !verification.ok) {
        toast.error(t(`lucaSettings.verify.${verification.code}`))
      } else {
        toast.success(t('lucaSettings.dialog.created'))
      }
      onCreated()
    },
    onError: (error: unknown) => toast.error(backendDetail(error) ?? t('lucaSettings.createError'))
  })

  const submit = () => {
    if (!selected) return
    const finalServerUrl = (serverUrl || selected.default_server_url || '').trim()
    if (!finalServerUrl) {
      toast.error(t('lucaSettings.dialog.serverUrlRequired'))
      return
    }
    if (isBasicPair && (!clientId.trim() || !clientSecret.trim())) {
      toast.error(t('lucaSettings.dialog.clientCredsRequired'))
      return
    }
    if (!isBasicPair && effectiveAuthType === 'token' && !token.trim()) {
      toast.error(t('lucaSettings.dialog.tokenRequired'))
      return
    }
    const wantsClientCreds = isBasicPair || effectiveAuthType === 'oauth'
    createMutation.mutate({
      template_key: selected.key,
      display_name: displayName.trim() || undefined,
      server_url: finalServerUrl,
      auth_type: selected.allow_auth_type_override ? authType : undefined,
      token: !isBasicPair && effectiveAuthType === 'token' ? token.trim() : undefined,
      token_scheme: !isBasicPair && effectiveAuthType === 'token' ? tokenScheme : undefined,
      client_id: wantsClientCreds && clientId.trim() ? clientId.trim() : undefined,
      client_secret: wantsClientCreds && clientSecret.trim() ? clientSecret.trim() : undefined,
      return_url: returnUrl
    })
  }

  return (
    // The parent mounts this component only while the dialog is open, so the modal is
    // always visible once rendered; closing is delegated upwards via `onClose`.
    <ModalV2
      isVisible
      header={t('lucaSettings.dialog.title')}
      handleOnClose={onClose}
      width={560}
      autoHeight
      footer={
        <>
          <Button type="button" isSecondary onClick={onClose}>
            {t('lucaSettings.dialog.cancel')}
          </Button>
          {selected && (
            <Button
              type="button"
              intent="primary"
              onClick={submit}
              showSpinner={createMutation.isPending}
              disabled={blocked}
            >
              {t('lucaSettings.dialog.connect')}
            </Button>
          )}
        </>
      }
    >
      {!selected ? (
        <>
          <SectionLabel>{t('lucaSettings.dialog.chooseProvider')}</SectionLabel>
          <TemplateGrid>
            {templates?.map(template => (
              <TemplateCard key={template.key} onClick={() => selectTemplate(template)}>
                <ProviderAvatar templateKey={template.key} size={2.2} />
                <TemplateText>
                  <TemplateName>
                    {template.name}
                    <AuthBadge>{t(`lucaSettings.authBadge.${template.auth_type}`)}</AuthBadge>
                  </TemplateName>
                  <TemplateDesc>{template.description}</TemplateDesc>
                </TemplateText>
              </TemplateCard>
            ))}
          </TemplateGrid>
        </>
      ) : (
        <>
          <SelectedHeader>
            <Button
              type="button"
              isSecondary
              size="sm"
              icon={<FaArrowLeft />}
              onClick={goBack}
              aria-label={t('lucaSettings.dialog.back')}
            />
            <ProviderAvatar templateKey={selected.key} size={2} />
            <ProviderName>{selected.name}</ProviderName>
          </SelectedHeader>

          <Form>
            {isCustom ? (
              <WarningBanner title={t('lucaSettings.dialog.customWarningTitle')}>
                {t('lucaSettings.dialog.customWarningBody')}
                <Acknowledge>
                  <Checkbox
                    id="mcp-acknowledge"
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                    label={t('lucaSettings.dialog.acknowledge')}
                  />
                </Acknowledge>
              </WarningBanner>
            ) : (
              <ExternalNote>{t('lucaSettings.dialog.externalNote')}</ExternalNote>
            )}
            <Field>
              <Input
                id="mcp-display-name"
                label={t('lucaSettings.dialog.name')}
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
            </Field>

            {selected.allow_server_url_override && (
              <Field>
                <Input
                  id="mcp-server-url"
                  label={t('lucaSettings.dialog.serverUrl')}
                  value={serverUrl}
                  onChange={e => setServerUrl(e.target.value)}
                  placeholder="https://mcp.example.com/mcp"
                />
              </Field>
            )}

            {selected.allow_auth_type_override && (
              <Field>
                <Select
                  id="mcp-auth-type"
                  name="mcp-auth-type"
                  label={t('lucaSettings.dialog.authType')}
                  value={authType}
                  onChange={e => setAuthType(e.target.value as McpAuthType)}
                >
                  <option value="oauth">{t('lucaSettings.dialog.oauth')}</option>
                  <option value="token">{t('lucaSettings.dialog.token')}</option>
                </Select>
              </Field>
            )}

            {isBasicPair && (
              <>
                <Field>
                  <Input
                    id="mcp-client-id"
                    label={t('lucaSettings.dialog.clientIdLabel')}
                    value={clientId}
                    onChange={e => setClientId(e.target.value)}
                    placeholder={t('lucaSettings.dialog.clientIdPlaceholder')}
                  />
                </Field>
                <Field>
                  <Input
                    id="mcp-client-secret"
                    type="password"
                    label={t('lucaSettings.dialog.clientSecretLabel')}
                    value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)}
                    placeholder={t('lucaSettings.dialog.clientSecretPlaceholder')}
                  />
                </Field>
                {selected.docs_url && (
                  <CredsHelp>
                    {t('lucaSettings.dialog.credentialsHelp')}{' '}
                    <a href={selected.docs_url} target="_blank" rel="noreferrer">
                      {t('lucaSettings.dialog.credentialsHelpLink')}
                    </a>
                  </CredsHelp>
                )}
              </>
            )}

            {!isBasicPair && effectiveAuthType === 'token' && (
              <>
                <Field>
                  <Input
                    id="mcp-token"
                    type="password"
                    label={selected.token_field_label ?? t('lucaSettings.dialog.token')}
                    value={token}
                    onChange={e => setToken(e.target.value)}
                    placeholder={t('lucaSettings.dialog.tokenPlaceholder')}
                  />
                </Field>
                <Field>
                  <Select
                    id="mcp-token-scheme"
                    name="mcp-token-scheme"
                    label={t('lucaSettings.dialog.tokenScheme')}
                    value={tokenScheme}
                    onChange={e => setTokenScheme(e.target.value)}
                  >
                    {TOKEN_SCHEMES.map(scheme => (
                      <option key={scheme} value={scheme}>
                        {scheme}
                      </option>
                    ))}
                  </Select>
                  <FieldHint>{t('lucaSettings.dialog.tokenSchemeHint')}</FieldHint>
                </Field>
              </>
            )}

            {effectiveAuthType === 'oauth' && (
              <>
                <SectionLabel>{t('lucaSettings.dialog.advanced')}</SectionLabel>
                <Field>
                  <Input
                    id="mcp-client-id"
                    value={clientId}
                    onChange={e => setClientId(e.target.value)}
                    placeholder={t('lucaSettings.dialog.clientId')}
                  />
                </Field>
                <Field>
                  <Input
                    id="mcp-client-secret"
                    type="password"
                    value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)}
                    placeholder={t('lucaSettings.dialog.clientSecret')}
                  />
                </Field>
              </>
            )}
          </Form>
        </>
      )}
    </ModalV2>
  )
}
